// Eén ingang voor de tekening van een stuk (O-120), voor de bouw-PDF en de kaart in de viewer. Puur.
//
// De tekening komt uit het 3D-model (core/modeltekening.ts) als de driehoeken er zijn en de maten uit het
// model gelijk zijn aan de zaaglijst van de motor: lengte, breedte, dikte, het aantal gaten en elke Ø. Dan
// komen de omtrek, de gaten (plek, Ø, diepte of door, vlak), de randen van een schuine kant en het
// zijaanzicht uit het model. Wijkt iets af, of is er geen model, dan blijft de tekening uit het ontwerp
// (`ontwerp`: gaten uit de boorbewerkingen, omtrek uit de zaagsneden) en zegt `afwijkingen` waarom.
//
// Wat uit het ontwerp blijft, ook met het model: de maten van de zaaglijst als buitenmaten, begin en eind
// (voor de tekst), en de sleuven. De Ø van een gat is die uit de zaaglijst (het model geeft hem op een
// paar honderdste mm; de controle heeft ze dan al gelijk bevonden).
// Gesloten ontwerppaden helpen een ronde uitsparing van een boorgat te onderscheiden (O-332).

import { onderdeelUitModel, vergelijkMetZaaglijst, type Geometrie, type ZaaglijstStuk } from "./modeltekening.ts";
import type { SchetsInvoer, SchetsPad } from "./maatschets.ts";

export type { Geometrie } from "./modeltekening.ts";

export interface StukTekening {
  schets: SchetsInvoer;
  bron: "model" | "ontwerp";
  /** Waarom de tekening niet uit het model komt; leeg bij bron "model". */
  afwijkingen: string[];
}

/**
 * De tekening van één stuk: uit het model als dat klopt met de zaaglijst, anders `ontwerp`. `zaaglijst`
 * geeft de maten van de regel en de gaten per stuk uit de bewerking (gatenUitBewerking). `ankers`: de punten
 * van de zaagsneden in het boven- of ondervlak uit het ontwerp (mm), om de omtrek op af te ronden.
 */
/** Zo dicht (mm) bij een punt van een zaagsnede uit het ontwerp komt een hoekpunt van het model op dat punt: de zaagbreedte. */
const OP_ONTWERP_MM = 1.6;

/**
 * Een hoekpunt van de omtrek uit het model op het punt van de zaagsnede uit het ontwerp, als dat binnen de
 * zaagbreedte ligt. Het model heeft de snede met de zaagbreedte erin (een boog van 160 mm wordt er 161,4);
 * de tekening geeft de maat waarop je aftekent, zoals de bewerking. Een punt op de rand van het blok blijft op die rand.
 */
function opOntwerp(omtrek: Array<[number, number]>, ankers: ReadonlyArray<readonly [number, number]>, L: number, B: number): Array<[number, number]> {
  const rand = (w: number, max: number) => (Math.abs(w) <= 0.05 ? 0 : Math.abs(w - max) <= 0.05 ? max : null);
  return omtrek.map((p): [number, number] => {
    let beste: readonly [number, number] | null = null;
    let min = OP_ONTWERP_MM;
    for (const a of ankers) {
      const d = Math.hypot(a[0] - p[0], a[1] - p[1]);
      if (d > min) continue;
      const rx = rand(p[0], L);
      const ry = rand(p[1], B);
      if ((rx !== null && Math.abs(a[0] - rx) > 0.05) || (ry !== null && Math.abs(a[1] - ry) > 0.05)) continue;
      min = d;
      beste = a;
    }
    return beste ? [beste[0], beste[1]] : p;
  });
}

/**
 * Een aanroeper kan een boogpad weglaten uit de getekende ontwerppaden, maar de bemonsterde
 * snijrand wel als ankers doorgeven. Gebruik die dan als kandidaat voor de classificatie.
 * Alleen een volledige ronde doorgaande binnenlus uit het model kan hiermee overeenkomen:
 * modeltekening vergelijkt beide volledige randen, inclusief de middens van alle lijnstukken.
 * Een onvolledige boog, losse hoekpunten of een verschoven snijrand zijn dus niet voldoende.
 * De kandidaat wordt nooit rechtstreeks getekend; de uiteindelijke rand komt uit het model.
 */
function modelPaden(ontwerp: SchetsInvoer, ankers: ReadonlyArray<readonly [number, number]>): SchetsPad[] {
  if (ontwerp.paden.length || ankers.length < 8) return ontwerp.paden;
  return [{
    punten: ankers.map(([x, y]): [number, number] => [x, y]),
    gesloten: true,
    onder: false,
  }];
}

export function tekeningVanStuk(ontwerp: SchetsInvoer, geo: Geometrie | null | undefined, zaaglijst: ZaaglijstStuk, ankers: ReadonlyArray<readonly [number, number]> = []): StukTekening {
  if (typeof ontwerp.diameter_mm === "number" && ontwerp.diameter_mm > 0) return { schets: ontwerp, bron: "ontwerp", afwijkingen: ["rond onderdeel: de tekening is de cirkel uit de zaaglijst"] };
  if (!geo || !geo.indices.length) return { schets: ontwerp, bron: "ontwerp", afwijkingen: ["geen 3D-model voor dit onderdeel"] };
  const m = onderdeelUitModel(geo, zaaglijst, {
    gaten: ontwerp.gaten,
    paden: modelPaden(ontwerp, ankers),
    ...(ontwerp.omtrek ? { omtrek: ontwerp.omtrek } : {}),
  });
  if (!m) return { schets: ontwerp, bron: "ontwerp", afwijkingen: ["de vorm is niet uit het 3D-model te halen"] };
  const afwijkingen = vergelijkMetZaaglijst(m, zaaglijst);
  if (afwijkingen.length) return { schets: ontwerp, bron: "ontwerp", afwijkingen };
  // De Ø uit de zaaglijst bij elk gat (de controle vond voor elk gat een gelijke).
  const over = zaaglijst.gaten.flatMap((g) => Array.from({ length: g.aantal }, () => g.diameter_mm));
  // De plek van een gat uit het model is op 0,01 mm afgerond en kan door de hoek van een schuine kop 0,01 mm naast
  // de plek in het ontwerp (de zaaglijst en de kaart) uitkomen. Bij een maat op een half tiende (278,55 in 346,3
  // geeft 67,75) staat er dan 67,7 op de tekening en 67,8 in de gegevens. Binnen 0,03 mm geldt de plek uit het ontwerp.
  const vrijeOntwerpGaten = [...ontwerp.gaten];
  const gaten = m.gaten.map((g) => {
    const i = over.findIndex((d) => Math.abs(d - g.diameter_mm) <= 0.2);
    const d = i >= 0 ? over.splice(i, 1)[0]! : g.diameter_mm;
    const n = vrijeOntwerpGaten.findIndex((o) =>
      o.vlak === g.vlak && Math.abs(o.diameter_mm - g.diameter_mm) <= 0.2 &&
      Math.abs(o.x_mm - g.x_mm) <= 0.03 && Math.abs(o.y_mm - g.y_mm) <= 0.03 && Math.abs((o.z_mm ?? 0) - (g.z_mm ?? 0)) <= 0.03);
    const o = n >= 0 ? vrijeOntwerpGaten.splice(n, 1)[0]! : null;
    return { ...g, ...(o ? { x_mm: o.x_mm, y_mm: o.y_mm, ...(o.z_mm !== undefined ? { z_mm: o.z_mm } : {}) } : {}), diameter_mm: d };
  });
  return {
    bron: "model",
    afwijkingen: [],
    schets: {
      ...ontwerp,
      omtrek: opOntwerp(m.omtrek, ankers, m.lengte_mm, m.breedte_mm),
      ...(zaaglijst.stralen?.length ? { stralen: zaaglijst.stralen } : {}),
      ...(m.lijnen.length ? { lijnen: m.lijnen } : {}),
      ...(m.zij ? { zij_omtrek: m.zij } : {}),
      gaten,
      // De vorm van een zaagsnede of uitsparing staat al in de omtrek; een uitsparing die door gaat, komt uit het model.
      paden: m.paden,
    },
  };
}
